import {
  FetchNextPageOptions,
  InfiniteData,
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useInfiniteQuery,
} from 'react-query';
import { cloneDeep } from 'lodash';
import { isNewWatchlist } from '../../pages/MyContent/WatchlistsTab/WatchlistsTab.logic';
import { ApiError } from '../../services/api-error.model';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { IssuersQueryParams } from '../../services/issuers/issuersQueryParams';
import { fetchIssuers } from '../../services/issuers/issuers.service';
import { queryKeys } from '../queryKeyFactory';
import { getNextPageParam } from '../../utils/infiniteQueryUtils';

interface UseFetchInfiniteIssuersResult {
  data?: IssuerListItemDto[];
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  error?: ApiError;
  hasNextPage?: boolean;
  fetchNextPage?: (options?: FetchNextPageOptions) => Promise<unknown>;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<InfiniteData<IssuerListItemDto>, any>>;
  isFetchingNextPage?: boolean;
  totalResults?: number;
}

export default (queryParams: IssuersQueryParams): UseFetchInfiniteIssuersResult => {
  const batchSize = queryParams.take ?? 150;
  let totalResults = 0;

  const query = useInfiniteQuery(
    queryKeys.fetchIssuers.query(queryParams),
    (options) => {
      if (queryParams.watchlistId && isNewWatchlist(queryParams.watchlistId)) {
        return { results: [], totalResults: 0 };
      }

      const params = cloneDeep(queryParams);
      params.skip = (options.pageParam ?? 0) * batchSize;
      params.take = batchSize;

      return fetchIssuers(params);
    },
    {
      select: (data) => {
        const pages = data.pages.flatMap((page) => page.results);
        totalResults = data.pages[0].totalResults;

        return { pages, pageParams: data.pageParams };
      },
      getNextPageParam,
      keepPreviousData: true,
    }
  );

  return {
    data: query.data?.pages?.flat(),
    isLoading: query.isLoading,
    isError: query.isError,
    isRefetching: query.isRefetching,
    error: query.error as ApiError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
    totalResults,
  };
};
